import { useCallback, useEffect, useMemo, useState } from "react";
import { TextField, InputAdornment, Tooltip } from "@mui/material";
import { getClientHealthData } from "../../api/clientHealth";
import { getRowHealthCells, getClientHealthRowSearchText } from "../../utils/clientHealthStatus";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import styles from "./ClientHealthPage.module.css";

function ClientHealthTableCell({ parsed }) {
  if (parsed.variant === "empty") {
    return (
      <td className={`${styles["ch-td"]} ${styles["ch-td-empty"]}`}>
        <span className={styles["ch-empty-text"]}>—</span>
      </td>
    );
  }
  const isSuccess = parsed.variant === "success";
  const hoverText = parsed.hoverText;

  const inner = (
    <div className={styles["ch-cell-inner"]}>
      <div className={styles["ch-status-row"]}>
        <span className={styles["ch-status-label"]}>Status</span>
        <span className={`${styles["ch-badge"]} ${isSuccess ? styles["ch-badge-success"] : styles["ch-badge-failed"]}`}>
          {parsed.label}
        </span>
      </div>
      <span className={styles["ch-time"]}>{parsed.lastFormatted}</span>
      <span className={styles["ch-sub"]}>Last Fetch Date & Time</span>
    </div>
  );

  return (
    <td className={`${styles["ch-td"]} ${isSuccess ? styles["ch-td-success"] : styles["ch-td-failed"]}`}>
      {hoverText ? (
        <Tooltip
          title={<span className={styles["ch-tooltip-body"]}>{hoverText}</span>}
          arrow
          placement="top"
          enterDelay={250}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 400,
                fontFamily: "Inter, sans-serif",
                fontSize: "0.8125rem",
                lineHeight: 1.45,
              },
            },
          }}
        >
          <span className={styles["ch-td-hover-wrap"]}>{inner}</span>
        </Tooltip>
      ) : (
        inner
      )}
    </td>
  );
}

function ClientHealthPage() {
  const [clientHealthRows, setClientHealthRows] = useState([]);
  const [clientHealthSearchQuery, setClientHealthSearchQuery] = useState("");
  const [clientHealthLoading, setClientHealthLoading] = useState(false);
  const [clientHealthError, setClientHealthError] = useState(null);

  const fetchClientHealth = useCallback(async () => {
    setClientHealthLoading(true);
    setClientHealthError(null);
    try {
      const data = await getClientHealthData();
      setClientHealthRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setClientHealthError(err?.response?.data?.detail || err?.message || "Failed to load client health");
      setClientHealthRows([]);
    } finally {
      setClientHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientHealth();
  }, [fetchClientHealth]);

  const filteredClientHealthRows = useMemo(() => {
    const q = clientHealthSearchQuery.trim().toLowerCase();
    if (!q) return clientHealthRows;
    return clientHealthRows.filter((row) => getClientHealthRowSearchText(row).includes(q));
  }, [clientHealthRows, clientHealthSearchQuery]);

  return (
    <div className={styles["container"]}>
      <div className={styles["pageCard"]}>
        <div className={styles["content-header"]}>
          <h1 className={styles["content-title"]}>Client Health</h1>
          <p className={styles["content-subtitle"]}>
            Monitor order, trade, position, and limit health across broker-linked accounts
          </p>
        </div>
        <div className={styles["search-row"]}>
          <TextField
            className={styles["search-field"]}
            placeholder="Search (client code, username, master, broker, errors, messages)…"
            value={clientHealthSearchQuery}
            onChange={(e) => setClientHealthSearchQuery(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            disabled={clientHealthLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": "Search client health" }}
          />
        </div>
        <div className={styles["client-health-table-wrap"]}>
          {clientHealthError ? (
            <div className={styles["client-health-table-error"]}>{clientHealthError}</div>
          ) : null}
          <table className={styles["client-health-table"]}>
            <thead>
              <tr>
                <th className={styles["client-health-th-broker"]}>Broker Code</th>
                <th className={styles["client-health-th-health"]}>Order Book Health</th>
                <th className={styles["client-health-th-health"]}>Trader Book Health</th>
                <th className={styles["client-health-th-health"]}>Position Book Health</th>
                <th className={styles["client-health-th-health"]}>Limit Health</th>
              </tr>
            </thead>
            <tbody>
              {clientHealthLoading ? (
                <tr>
                  <td colSpan={5} className={styles["client-health-loading"]}>
                    Loading client health…
                  </td>
                </tr>
              ) : clientHealthRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className={styles["client-health-empty"]}>
                    No client health data returned.
                  </td>
                </tr>
              ) : filteredClientHealthRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className={styles["client-health-empty"]}>
                    No rows match your search.
                  </td>
                </tr>
              ) : (
                filteredClientHealthRows.map((row, rowIdx) => {
                  const cells = getRowHealthCells(row);
                  const rowKey = `${row.broker_client_code ?? "row"}-${row.username ?? rowIdx}`;
                  return (
                    <tr key={rowKey} className={styles["client-health-row"]}>
                      <td className={styles["ch-td-broker"]}>
                        <span className={styles["ch-broker-code"]}>{row.broker_client_code ?? "—"}</span>
                        {row.full_name ? (
                          <span className={styles["ch-client-name"]}>{row.full_name}</span>
                        ) : null}
                        {row.broker_name ? (
                          <span className={styles["ch-broker-name"]}>{row.broker_name}</span>
                        ) : null}
                      </td>
                      {cells.map((cell) => (
                        <ClientHealthTableCell key={cell.key} parsed={cell} />
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ClientHealthPage;
